import React, { useContext, useState } from 'react'
import { FormikErrors, useFormik } from 'formik'
import _get from 'lodash/get'
import Input from '../forms/Input'
import Button from '../Button'
import { Text } from '@src/components/Text'
import { ModalContext, ModalType } from './ModalController'
import { color, layout, space } from 'styled-system'
import styled from '@emotion/styled'
import axios from 'axios'
import { Box, Flex } from '../Boxes'
import { css } from '@emotion/core'
import Translate from '@src/components/translation/Translate'
import { TranslationContext } from '@src/components/translation/TranslationContext'
import loadingMojo from '@src/assets/images/loadingMojo.gif'
import { setEntityId } from '@src/utils/logClient'

const Link = styled.a`
  display: inline-block;
  line-height: 18px;
  font-family: proxima-nova, 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 600;
  color: ${props => (props.color ? props.color : '#adadad')};
  text-decoration: none;
  cursor: pointer;
  &:hover,
  &:focus {
    text-decoration: none;
    color: ${props => (props.hoverColor ? props.hoverColor : '#00bcf2')}
  }

  ${layout}
  ${space}
  ${color}
`

const StyledForm = styled('form')`
  width: 100%;
`

type ApiAnswerState = 'success' | 'api-error' | 'auth-error' | null

const ApiAnswerDetails = ({ apiAnswer, showForgotPasswordModal }: { apiAnswer: ApiAnswerState; showForgotPasswordModal: () => void }) => {
  if (apiAnswer === null) return null

  const colorCss =
    apiAnswer === 'success'
      ? css`
          background-color: #dff0d8;
          border-color: #d6e9c6;
          color: #3c763d;
          border-radius: 5px;
        `
      : css`
          background-color: #f2dede;
          border-color: #ebccd1;
          color: #a94442;
          border-radius: 5px;
        `
  return (
    <Box my={3} p={3} textAlign="center" css={colorCss}>
      {apiAnswer === 'success' && (
        <Text fontSize="2">
          <Translate path="components.login.login_success"/>
        </Text>
      )}
      {apiAnswer === 'api-error' && (
        <>
          <Text fontSize="2" display="inline">
            <Translate path="components.login.login_error"/>
          </Text>
          <Link ml="5px" href="http://status.classdojo.com" color="#00bcf2" hoverColor="#00a8d9" display="inline">
            <Translate path="components.login.check_status_page"/>
          </Link>
        </>
      )}
      {apiAnswer === 'auth-error' && (
        <>
          <Text fontSize="2" display="inline">
            <Translate path="components.login.invalid_email_or_password"/>
          </Text>
          <Link ml="5px" onClick={showForgotPasswordModal} color="#00bcf2" hoverColor="#00a8d9" display="inline">
            <Translate path="components.login.did_you_forget"/>
          </Link>
        </>
      )}
    </Box>
  )
}

type LoginFormData = {
  login: string
  password: string
}
type LoginFormProps = {
  userType: 'teacher' | 'parent' | 'leader'
}

const LoginForm: React.FC<LoginFormProps> = ({ userType }) => {
  const t = useContext(TranslationContext)
  const modalContext = useContext(ModalContext)

  function showForgotPasswordModal() {
    modalContext.showModal(ModalType.ResetPassword)
  }

  function showSignupModal() {
    modalContext.showModal(ModalType.Signup)
  }

  const [apiAnswer, setApiAnswer] = useState<ApiAnswerState>(null)

  const form = useFormik<LoginFormData>({
    initialValues: { login: '', password: '' },
    onSubmit: (values, { setSubmitting }) => {
      const baseUrl = userType === 'teacher' ? process.env.GATSBY_TEACHER_LOGIN_URL : process.env.GATSBY_DEFAULT_LOGIN_URL
      const url = `${baseUrl}/api/session`
      axios
        .post(
          url,
          {
            ...values,
            resumeAddClassFlow: !!window?.location?.href?.match(/\/connect/)
          },
          {
            withCredentials: true
          }
        )
        .then((response: any) => {
          setApiAnswer('success')
          const entity = response.teacher || response.parent || response.student
          if (entity) {
            setEntityId(entity._id)
          }
          let redirectUrl = 'https://home.classdojo.com/'
          if (response.data.type === 'teacher') redirectUrl = 'https://teach.classdojo.com'
          else if (response.data.type === 'student') redirectUrl = 'https://student.classdojo.com'
          window.location.href = redirectUrl
        })
        .catch(error => {
          console.log('login error', error)
          if (_get(error, 'response.status') === 401) setApiAnswer('auth-error')
          else setApiAnswer('api-error')
        })
        .then(function() {
          setSubmitting(false)
        })
    },
    validate: (values: LoginFormData) => {
      const errors: FormikErrors<LoginFormData> = {}
      if (values.login.length === 0) {
        errors.login = 'length'
      }
      if (values.password.length === 0) {
        errors.password = 'length'
      }
      return errors
    }
  })

  return (
    <StyledForm onSubmit={form.handleSubmit} data-test-name="login-form">
      {form.isSubmitting && (
        <Box width="50px" height="50px" mb={4} mx="auto">
          <img src={loadingMojo}></img>
        </Box>
      )}
      <Input
        placeholder={t.translate('components.common.email_address') as string}
        autoComplete="email"
        marginBottom={15}
        name="login"
        onChange={form.handleChange}
        value={form.values.login}
        success={!form.errors.login && form.touched.login}
        error={form.errors.login && form.touched.login}
        onBlur={form.handleBlur}
      />
      <Input
        placeholder={t.translate('components.common.password') as string}
        autoComplete="off"
        type="password"
        name="password"
        onChange={form.handleChange}
        value={form.values.password}
        success={!form.errors.password && form.touched.password}
        error={form.errors.password && form.touched.password}
        onBlur={form.handleBlur}
      />
      <Link onClick={showForgotPasswordModal} width="100%" mb="10px">
        {t.translate('components.login.forget_password')}
      </Link>
      <Button
        disabled={form.isSubmitting || !form.isValid || !form.dirty}
        type="submit"
        big square
        width="100%"
        my="5px"
      >
        {t.translate('pages.home.log_in')}
      </Button>
      <ApiAnswerDetails apiAnswer={apiAnswer} showForgotPasswordModal={showForgotPasswordModal}/>
      <Flex justifyContent="center">
        <Text fontSize={2}> {t.translate('components.login.dont_have_account')}</Text>
        <Link onClick={showSignupModal} ml="10px">
          {t.translate('pages.home.sign_up')}
        </Link>
      </Flex>
    </StyledForm>
  )
}

export default LoginForm
